// ABOUTME: Applies the Jocko design skin to the Recharge subscription-widget-v2 shadow DOM
// (purchase options, benefits list, selling-plans dropdown) — the theme-owned styling the live
// theme injected from product-info.js addRechargeCSSOverride() — plus a discount badge on the
// Subscribe & Save option (D80, the build's own: live's widget has none), and, on a variant
// dropdown change, returns the widget to Subscribe & Save with its default plan and the quantity
// to its minimum, as live's page load does; the widget markup itself is app-owned (Recharge
// Theme App Extension app block).

const WIDGET_TAG = 'recharge-subscription-widget';

// The Subscribe & Save badge's words and colours (D80, register 53-R43), in one place: the
// discount of the plan the option prices, as a pill in the gallery badge's look
// (snippets/jf-media-badges.liquid: Reload Medium 14px, the sale red, full round).
const BADGE = {
  text: (percent) => `${percent}% OFF`,
  color: '#FFFFFF',
  background: '#E7131A',
};

// Carries the badge's text from the widget element (light DOM, which Recharge never writes)
// into its shadow DOM, where the pill's rule reads it; unset, the option draws no pill.
const BADGE_TEXT_PROPERTY = '--jf-rc-badge-text';

// Live payload 1 — outer widget root: purchase-option chrome + prices.
const STYLE_WIDGET = `
  .rc-purchase-option {
    border: 1px solid #F5F3F2 !important;
  }
  .rc-purchase-option.rc-purchase-option__selected {
    background-color: #F5F3F2;
    border: 1px solid #F5F3F2;
    padding: 20px;
  }
  .rc-purchase-option__label {
    align-items: start !important;
  }
  .rc-purchase-option__selector.rc-purchase-option__selector {
    font-size: 18px;
    line-height: 1.125;
    font-family: 'Reload Medium', sans-serif;
    font-weight: 400;
  }
  .rc-purchase-option__prices {
    flex-direction: column-reverse !important;
    gap: 10px;
    padding-top: 6px;
  }
  .rc-purchase-option__prices .rc-price {
    font-size: 14px;
    line-height: 1.2;
    font-family: 'OCR B Std', sans-serif;
    font-weight: 400;
  }
  /* Not live's red (D139, Codal A27): #C31016 gives 5.57:1 on the selected option's #F5F3F2. */
  .rc-purchase-option__prices .rc-price[part="rc-purchase-option__discounted-price"] {
    color: #C31016;
  }
  .rc-purchase-option__prices .text-muted {
    color: #231F20;
  }
  .rc-purchase-option__checked-indicator {
    width: 20px;
    max-width: 20px;
    height: 20px;
    padding: 3px 0;
    align-items: center;
    justify-content: center;
    margin-right: 16px!important;
  }
  .rc-purchase-option__label {
    padding: 20px 20px 16px!important;
  }
  .rc-purchase-option__sub-container {
    gap: 16px!important;
  }
  /* Not in live's payload (D80): the discount pill, on the Subscribe & Save option's top border
     over its prices, where Recharge places its own badge. Generated content from this style, so a
     re-render that replaces the option's nodes draws it again; absolutely positioned, so it takes
     no room in the widget. About 25px tall, it straddles the border inside the 20px gap above
     the widget and ends above the prices. Recharge's own badge is hidden, so the option carries
     one badge if its badge text is ever set in the app. */
  .rc-purchase-option__subscription {
    position: relative;
  }
  .rc-purchase-option__subscription::before {
    content: var(${BADGE_TEXT_PROPERTY}, none);
    position: absolute;
    top: 0;
    right: 20px;
    z-index: 1;
    transform: translateY(-50%);
    padding: 4px 12px;
    border-radius: 100px;
    background-color: ${BADGE.background};
    color: ${BADGE.color};
    font-family: 'Reload Medium', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 120%;
    white-space: nowrap;
    pointer-events: none;
  }
  .rc-purchase-option__subscription .rc-purchase-option__badge {
    display: none;
  }
`;

// Live payload 2 — rc-benefits shadow root: the benefits list set in the Recharge app.
const STYLE_BENEFITS = `
  .rc-benefits__list {
    font-size: 14px;
    color: #121212;
  }
  .rc-benefits__list li {
    margin-bottom: 8px!important;
    --rc-widget-bullet-icon-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAxOCAxOCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTYuOTE4NjkgMTEuMjExNEw0LjE3NzE5IDguNTQ2NEwzIDkuNjkwNzJMNi45MTg2OSAxMy41TDE1IDUuNjQ0MzJMMTMuODIyOCA0LjVMNi45MTg2OSAxMS4yMTE0WiIgZmlsbD0iIzM5MzUzNiIvPgo8L3N2Zz4K);
  }
  .rc-benefits__list li::before {
    top: 0;
    mask-position: center;
    mask-size: 100%;
    width: 1.125em;
    height: 1.1125em;
  }
  .rc-benefits__list li,
  .rc-benefits__list p {
    font-size: 16px;
    line-height: 1.375;
    font-weight: normal;
    font-family: 'Ciutadella', sans-serif;
  }
  .rc-benefits__list p {
    margin: 0!important;
  }
  .rc-benefits__list li:last-child {
    margin: 0!important;
  }
`;

// Live payload 3 — rc-selling-plans shadow root: delivery-interval label + dropdown.
const STYLE_SELLING_PLANS = `
  .rc-plans__label {
    font-size: 18px!important;
    line-height: 1.33!important;
    font-family: 'Reload Medium', sans-serif!important;
    font-weight: 400!important;
    margin-bottom: 8px;
    display: block;
  }
  .rc-plans-dropdown__select {
    border: 0!important;
    width: auto;
    display: inline-block;
    font-size: 18px;
    line-height: 1.33;
    font-family: 'Ciutadella', sans-serif;
    padding-left: 0;
    padding: 0 32px 0 0;
  }
  .rc-plans-dropdown div {
    display: inline-block;
    width: auto;
  }
  /* Not in live's payload: from 750px the buy box is a column beside the gallery, narrower
     than live's buy box at the same widths (live's holds a 527px select), so the select,
     as wide as its longest plan name, stops at its column instead of pushing the page
     sideways. Below 750px it keeps live's width and the product section clips it at the
     screen edge, as live does (blocks/jf-recharge-widget-skin.liquid). */
  @media screen and (min-width: 750px) {
    .rc-plans-dropdown div,
    .rc-plans-dropdown__select {
      max-width: 100%;
    }
  }
`;

const makeStyle = (text) => {
  const el = document.createElement('style');
  el.textContent = text;
  return el;
};

// The nested rc-benefits / rc-selling-plans elements mount inside the widget's
// .rc-purchase-option__sub-container after the app boots; watch for that subtree
// exactly like the live MutationObserver did, then style each nested shadow root.
function skinNestedRoots(widgetRoot) {
  const styleForBenefits = makeStyle(STYLE_BENEFITS);
  const styleForSellingPlans = makeStyle(STYLE_SELLING_PLANS);

  const apply = (container, obs) => {
    const options = [...container.children];
    const benefits = options.find((option) => option.tagName?.toLowerCase() === 'rc-benefits');
    const sellingPlans = options.find((option) => option.tagName?.toLowerCase() === 'rc-selling-plans');
    // whenDefined never settles for a tag the app does not register, so wait only on the
    // elements this container actually mounted. With neither of them there yet there is
    // nothing to skin, and the watch stays up for the mount that brings them.
    const defined = [];
    if (benefits) defined.push(customElements.whenDefined('rc-benefits'));
    if (sellingPlans) defined.push(customElements.whenDefined('rc-selling-plans'));
    if (!defined.length) return;

    Promise.all(defined).then(() => {
      requestAnimationFrame(() => {
        let skinned = false;
        if (benefits?.shadowRoot) {
          benefits.shadowRoot.appendChild(styleForBenefits.cloneNode(true));
          skinned = true;
        }
        if (sellingPlans?.shadowRoot) {
          sellingPlans.shadowRoot.appendChild(styleForSellingPlans.cloneNode(true));
          skinned = true;
        }
        if (skinned) obs?.disconnect();
      });
    });
  };

  const observer = new MutationObserver((mutations, obs) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (
          node.nodeType === 1 &&
          node.classList.contains('rc-widget__root') &&
          node.querySelector('.rc-purchase-option__sub-container')
        ) {
          apply(node.querySelector('.rc-purchase-option__sub-container'), obs);
        }
      }
    }
  });

  observer.observe(widgetRoot, { childList: true, subtree: true });

  // In case the widget content is already mounted when this module runs.
  const existing = widgetRoot.querySelector('.rc-purchase-option__sub-container');
  if (existing) apply(existing, observer);
}

// The badge's text for the plan the Subscribe & Save option prices: Recharge's own pick of it,
// selectedVariantPlan (the variant's plan matching the selected selling plan, else the variant's
// first plan, which the option prices while One-time is on). Percentage discounts only; a plan
// with any other kind of discount, or none, gets no badge.
function badgeText(widget) {
  const plan = widget.selectedVariantPlan;
  const percent = plan && plan.discount_type === 'percentage' ? Number(plan.discount_amount) : 0;
  return percent > 0 ? BADGE.text(percent) : '';
}

function showBadge(widget) {
  const text = badgeText(widget);
  const value = text ? JSON.stringify(text) : '';
  if (widget.style.getPropertyValue(BADGE_TEXT_PROPERTY) === value) return;
  if (value) widget.style.setProperty(BADGE_TEXT_PROPERTY, value);
  else widget.style.removeProperty(BADGE_TEXT_PROPERTY);
}

// A plan pick, a purchase-option pick, a variant switch and the D72 reset each re-render the
// widget's shadow DOM (the option's prices, its rc-selected attribute), so every batch of changes
// there re-reads the plan and keeps the badge's text in step with it.
function keepBadge(widget, widgetRoot) {
  showBadge(widget);
  new MutationObserver(() => showBadge(widget)).observe(widgetRoot, { childList: true, subtree: true, characterData: true, attributes: true });
}

function skinWidget(widget) {
  customElements.whenDefined(WIDGET_TAG).then(() => {
    const widgetRoot = widget.shadowRoot;
    if (!widgetRoot) return;
    // The widget is a Lit element: it renders after a marker it appends to its shadow root and,
    // when it swaps its loading view for the purchase options, removes everything from that marker
    // on. This module runs after Recharge's script, once the loading view is up, so the style goes
    // first, ahead of the marker, where live's (appended before the first render) ends up too.
    widgetRoot.prepend(makeStyle(STYLE_WIDGET));
    skinNestedRoots(widgetRoot);
    keepBadge(widget, widgetRoot);
  });
}

// A combined-listing product switch mounts a fresh widget element (variant-picker.js remounts it so
// the app runs its lifecycle for the new product); the new shadow root needs the skin again.
document.addEventListener('jf:subscription-widget-remounted', (event) => {
  const remounted = event.detail && event.detail.widget;
  if (remounted && remounted.tagName && remounted.tagName.toLowerCase() === WIDGET_TAG) skinWidget(remounted);
});

// Selects the plan a freshly loaded widget opens on: the app block's default-selling-plan-id (it
// carries a ?selling_plan= deep link) where the dropdown offers it, else the first plan.
function pickDefaultPlan(widget, select) {
  const preferred = widget.getAttribute('default-selling-plan-id');
  const index = Math.max(0, [...select.options].findIndex((option) => option.value === preferred));
  if (select.selectedIndex === index) return;
  select.selectedIndex = index;
  select.dispatchEvent(new Event('change', { bubbles: true }));
}

// The plan dropdown lives in its own shadow root and exists only while Subscribe & Save is on, so
// right after that radio is checked it appears a render later: look for it for a few frames.
function withPlanSelect(root, callback, frames = 30) {
  const plans = root.querySelector('rc-selling-plans');
  const select = plans && plans.shadowRoot && plans.shadowRoot.querySelector('select');
  if (select) callback(select);
  else if (frames > 0) requestAnimationFrame(() => withPlanSelect(root, callback, frames - 1));
}

// Puts the widget on the selection a freshly loaded one shows: Subscribe & Save (the store's Recharge
// default) with its default plan. It dispatches the change events the widget's own handlers listen
// for, so the widget updates the product form's selling_plan input itself; the per-serving pill's
// listener on the radio hears the same event.
function resetToDefaults(widget) {
  const root = widget.shadowRoot;
  const subscription = root && root.querySelector('.rc-purchase-option__input[value="subscription"]');
  if (!subscription) return;
  if (!subscription.checked) {
    subscription.checked = true;
    subscription.dispatchEvent(new Event('change', { bubbles: true }));
  }
  withPlanSelect(root, (select) => pickDefaultPlan(widget, select));
}

// The product form's stepper back to the quantity a freshly rendered page opens on, its minimum (1 on
// this store, which sets no quantity rules), through the stepper's own methods so its buttons and the
// listeners of its update event follow; the input it holds is the quantity the form submits.
function resetQuantity(section) {
  const stepper = section.querySelector('product-form-component quantity-selector-component');
  if (!stepper || typeof stepper.getCurrentValues !== 'function') return;
  const { min, value } = stepper.getCurrentValues();
  if (value === min) return;
  stepper.setValue(String(min));
  stepper.onQuantityChange();
  stepper.updateButtonStates();
}

// Live's variant dropdown (Flavor, Servings) loads the product page for the picked variant, so its
// widget always comes back on those defaults whatever option or plan the shopper had picked (D72),
// and its quantity on 1 (D77). The build switches variants in place and keeps both, so the product
// page's own dropdown resets them here, and only on the shopper's change of that dropdown; the scroll
// position and the rest of the page keep their state. The reset runs before the section fetch
// returns, so the widget shows the default plan's prices by the time the per-serving pill reprices
// from them for the new variant.
document.addEventListener('change', (event) => {
  const picker = event.target instanceof HTMLSelectElement ? event.target.closest('variant-picker') : null;
  if (!picker || picker.dataset.templateProductMatch !== 'true' || picker.closest('product-card, quick-add-dialog')) return;
  const section = picker.closest('.shopify-section');
  if (!section) return;
  const widget = section.querySelector(WIDGET_TAG);
  if (widget) resetToDefaults(widget);
  resetQuantity(section);
});

const widget = document.querySelector(WIDGET_TAG);
if (widget) {
  skinWidget(widget);
} else {
  // The app block renders the element server-side, but guard the module against
  // running before the section markup lands (module timing is not Dawn's
  // product-info constructor timing).
  const bodyObserver = new MutationObserver((mutations, obs) => {
    const found = document.querySelector(WIDGET_TAG);
    if (found) {
      obs.disconnect();
      skinWidget(found);
    }
  });
  bodyObserver.observe(document.body, { childList: true, subtree: true });
  // Products without subscriptions never mount the widget; stop paying the
  // body-subtree observation tax once any realistic mount window has passed.
  setTimeout(() => bodyObserver.disconnect(), 30000);
}
